import React, { useState } from "react";
import { Plus, Search, FileText, Clock, CheckCircle, XCircle, X } from "lucide-react";
import { useLeaves } from "../hooks/useLeaves";
import { LeaveRequestsTable } from "../components/leaves/LeaveRequestsTable";
import { LeaveApplyModal } from "../components/leaves/LeaveApplyModal";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { HRStatCard } from "../components/HRStatCard";
import { LEAVE_STATUS, LEAVE_TYPES } from "../constants/hrConstants";
import toast from "react-hot-toast";

export function LeavesScreen() {
  const {
    leaves, stats, employees,
    filterStatus, setFilterStatus, filterType, setFilterType,
    filterEmp, setFilterEmp,
    isApplyOpen, isSaving,
    formData, formErrors,
    openApply, closeApply, updateField, save,
    approve, reject, cancel, deleteLeave,
  } = useLeaves();

  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [searchVisible, setSearchVisible] = useState(false);

  const handleApprove = async (id) => {
    await approve(id);
    toast.success("Leave approved");
  };

  const handleReject = async (id) => {
    await reject(id);
    toast.error("Leave rejected");
  };

  const handleCancel = async (id) => {
    await cancel(id);
    toast("Leave cancelled");
  };

  const handleDelete = (id) => {
    setDeleteTargetId(id);
  };

  const executeDelete = () => {
    if (deleteTargetId) {
      deleteLeave(deleteTargetId);
      toast.success("Leave request deleted");
      setDeleteTargetId(null);
    }
  };

  const handleSave = async () => {
    const ok = await save();
    if (ok) toast.success("Leave request submitted");
  };

  return (
    <>
      <div className="space-y-6 animate-in fade-in duration-400 bg-slate-50 min-h-screen pb-10">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <HRStatCard label="Total Requests" value={stats.total} icon={FileText} color="blue" />
        <HRStatCard label="Pending" value={stats.pending} icon={Clock} color="amber" />
        <HRStatCard label="Approved" value={stats.approved} icon={CheckCircle} color="green" />
        <HRStatCard label="Rejected" value={stats.rejected} icon={XCircle} color="red" />
      </div>

      {/* Control Panel */}
      <div className="card p-2 sm:p-3 flex flex-wrap items-center gap-2 bg-white shadow-sm border border-slate-100 animate-in fade-in slide-in-from-top-1 rounded-md">

        {/* Search Toggle */}
        <button
          onClick={() => { setSearchVisible(v => !v); if (searchVisible) setFilterEmp(''); }}
          className={`p-2 h-9 w-9 flex items-center justify-center rounded-md border transition-all ${
            searchVisible
              ? 'bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-200'
              : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-300'
          }`}
          title="Search Employee"
        >
          <Search size={16} />
        </button>

        {/* Status Filter */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[120px] max-w-[160px] w-auto"
            value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            {Object.entries(LEAVE_STATUS).map(([, v]) => (
              <option key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</option>
            ))}
          </select>
        </div>

        {/* Type Filter */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 hidden md:block">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[130px] max-w-[170px] w-auto"
            value={filterType} onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Leave Types</option>
            {LEAVE_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        {/* Apply Leave Button */}
        <div className="ml-auto">
          <button
            onClick={openApply}
            className="h-9 flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200 hover:scale-105 active:scale-95 transition-all whitespace-nowrap"
          >
            <Plus size={16} /> Apply Leave
          </button>
        </div>
      </div>

      {/* Search Overlay */}
      {searchVisible && (
        <div className="card p-3 flex items-center gap-3 bg-white shadow-sm border border-slate-100 rounded-md animate-in fade-in slide-in-from-top-1">
          <Search size={16} className="text-slate-400 shrink-0" />
          <input
            type="text"
            autoFocus
            className="flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
            placeholder="Search employee..."
            value={filterEmp}
            onChange={(e) => setFilterEmp(e.target.value)}
          />
          {filterEmp && (
            <button onClick={() => setFilterEmp('')} className="text-slate-400 hover:text-slate-600">
              <X size={15} />
            </button>
          )}
        </div>
      )}

      {/* Table */}
      <LeaveRequestsTable
        leaves={leaves}
        employees={employees}
        onApprove={handleApprove}
        onReject={handleReject}
        onCancel={handleCancel}
        onDelete={handleDelete}
      />
      </div>

      {/* Modal */}
      <LeaveApplyModal
        isOpen={isApplyOpen}
        onClose={closeApply}
        employees={employees}
        formData={formData}
        formErrors={formErrors}
        onFieldChange={updateField}
        onSubmit={handleSave}
        isSaving={isSaving}
      />
      <DeleteConfirmModal
        isOpen={Boolean(deleteTargetId)}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={executeDelete}
        title="Delete Leave Request"
        message="Are you sure you want to delete this leave request? This action cannot be undone."
      />
    </>
  );
}
